import {lazy, Suspense} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDomReady} from '../../../lib/utils/platform'

import {ContentfulHeroLayout} from './ContentfulHeroLayout'
import type {NinetailedExperienceEntry} from '../../../lib/utils/contentful-ninetailed'
import type {ContentfulHeroProps} from './contentful-hero.types'

// Lazy load Ninetailed experience orchestrator to prevent SSR bundling (same pattern as IntroHero)
const ContentfulHeroExperiences = lazy(() => import('./ContentfulHeroExperiences'))

export function ContentfulHero({component, paddingBlockEnd, analyticsContext, ...props}: ContentfulHeroProps) {
  // Check for Ninetailed experiences - only if feature flag is enabled
  const isNinetailedEnabled = isFeatureEnabled('landing_pages_ninetailed')
  const isDomReady = useDomReady()

  // Extract experiences (empty array if not enabled or not present)
  const rawExperiences: NinetailedExperienceEntry[] =
    isNinetailedEnabled && Array.isArray(component.fields.nt_experiences) ? component.fields.nt_experiences : []

  // Baseline component - used for SSR and when no experiences exist
  const baselineLayout = (
    <ContentfulHeroLayout
      component={component}
      paddingBlockEnd={paddingBlockEnd}
      analyticsContext={analyticsContext}
      {...props}
    />
  )

  // If no experiences, just render baseline directly (works for both SSR and client)
  if (rawExperiences.length === 0) {
    return baselineLayout
  }

  // During SSR or before DOM is ready, render the baseline for SEO/initial paint
  if (!isDomReady) {
    return null
  }

  // With experiences and DOM ready, lazy load the Ninetailed orchestrator
  // The experiences component handles the fade-in animation once Ninetailed resolves
  return (
    <Suspense fallback={null}>
      <ContentfulHeroExperiences
        component={component}
        paddingBlockEnd={paddingBlockEnd}
        analyticsContext={analyticsContext}
        rawExperiences={rawExperiences}
        {...props}
      />
    </Suspense>
  )
}

try{ ContentfulHeroExperiences.displayName ||= 'ContentfulHeroExperiences' } catch {}
try{ ContentfulHero.displayName ||= 'ContentfulHero' } catch {}